import { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  addRemotePairFromWords,
  ensureLocalPairForSetup,
  getStoredPairs,
  parseAndValidatePairingWords,
  removePair,
} from '../utils/pairing';
import { usePairingWordsContext } from '../contexts/PairingWordsContext';
import { useIdentity } from '../hooks/useIdentity';
import { usePairingConnected } from '../hooks/usePairingConnected';
import { getStoredRelays } from '../utils/relay';
import { runDevice2Confirmation } from '../services/pairingConfirm';
import { WordInputGrid } from './WordInputGrid';

export function PairingDisplayScreen(): JSX.Element {
  const ctx = usePairingWordsContext();
  const { identity, isLoading, createNewIdentity } = useIdentity();
  const { connected: pairingConnected } = usePairingConnected();
  const [words2nd, setWords2nd] = useState<string[]>([]);
  const [wordInput, setWordInput] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [justConnected, setJustConnected] = useState(false);
  const [visibleWordIndices, setVisibleWordIndices] = useState<Set<number>>(new Set());

  const toggleWordVisibility = (index: number): void => {
    setVisibleWordIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handleGenerateAnotherKey = (): void => {
    const pairs = getStoredPairs();
    const local = pairs.find((p) => p.is_local);
    if (local !== undefined) {
      removePair(local.uuid);
    }
    createNewIdentity();
  };

  useEffect(() => {
    if (identity !== null && identity.publicKey !== undefined) {
      const w = ensureLocalPairForSetup(identity.publicKey);
      setWords2nd(w);
    }
  }, [identity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoading || identity !== null) {
      return;
    }
    createNewIdentity();
  }, [isLoading, identity, createNewIdentity]);

  useEffect(() => {
    ctx?.setOfferWords(words2nd);
    return () => {
      ctx?.setOfferWords(null);
    };
  }, [ctx, words2nd]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    void (async (): Promise<void> => {
      setError(null);
      const wordsText = wordInput.join(' ');
      const parsed = parseAndValidatePairingWords(wordsText);
      if (parsed === null) {
        setError('Mots invalides. 17 mots requis.');
        return;
      }
      const pair = addRemotePairFromWords(parsed, []);
      if (pair === null) {
        setError('Mots invalides. Vérifiez la saisie.');
        return;
      }
      const pairs = getStoredPairs();
      const local = pairs.find((p) => p.is_local);
      const remote = pairs.find((p) => !p.is_local);
      if (
        identity === null ||
        local === undefined ||
        remote === undefined ||
        identity.privateKey === undefined
      ) {
        setSuccess(true);
        return;
      }
      const relays = getStoredRelays().filter((r) => r.enabled);
      if (relays.length === 0) {
        setError('Aucun relais activé. Configurez les relais pour finaliser le pairing.');
        return;
      }
      // Use pair's publicKey if available (will be updated from signatures if not)
      const remotePublicKey = remote.publicKey;
      setIsConfirming(true);
      try {
        const ok = await runDevice2Confirmation({
          pairLocal: local.uuid,
          pairRemote: remote.uuid,
          identity,
          relays,
          start: identity.t0_anniversaire,
          end: Date.now(),
          remotePublicKey,
        });
        setJustConnected(ok);
      } catch (err) {
        console.error('Pairing confirmation (device 2):', err);
        setError(
          err instanceof Error ? err.message : 'Erreur lors de la confirmation du pairing.',
        );
        setIsConfirming(false);
        return;
      }
      setIsConfirming(false);
      setSuccess(true);
    })();
  };

  if (success) {
    const showConnected = pairingConnected || justConnected;
    return (
      <main>
        {showConnected && (
          <p role="status" style={{ fontWeight: 'bold', color: 'var(--color-ok, green)' }}>
            Connecté
          </p>
        )}
        <h1>Pair associé</h1>
        <p>Le pair du 1ᵉʳ appareil a été ajouté.</p>
        <div
          role="region"
          aria-labelledby="words-2nd-heading"
          id="mots-2e-appareil"
          style={{
            display: 'block',
            marginTop: '1.5rem',
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: 'var(--color-info-bg, #dbeafe)',
            border: '2px solid var(--color-info-border, #93c5fd)',
            borderRadius: '8px',
            color: 'var(--color-text)',
          }}
        >
          <h2 id="words-2nd-heading" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
            Mots du 2ᵉ appareil — à copier sur le 1ᵉʳ
          </h2>
          <p style={{ marginTop: 0, marginBottom: '0.5rem' }}>
            <button
              type="button"
              onClick={handleGenerateAnotherKey}
              aria-label="Générer une autre clé publique"
            >
              Générer une autre clé publique
            </button>
          </p>
          {words2nd.length > 0 ? (
            <div
              aria-label="Mots 2e appareil"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                fontFamily: 'monospace',
                fontSize: '1rem',
                marginTop: '0.5rem',
                padding: '0.75rem',
                backgroundColor: 'var(--color-background)',
                borderRadius: '4px',
                border: '1px solid var(--color-info-border, #93c5fd)',
              }}
            >
              {words2nd.map((word, index) => {
                const visible = visibleWordIndices.has(index);
                return (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      backgroundColor: 'var(--color-info-bg, #dbeafe)',
                      borderRadius: '4px',
                      border: '1px solid var(--color-info-border, #93c5fd)',
                    }}
                  >
                    <span style={{ fontWeight: 'bold' }}>{index + 1}.</span>
                    <span style={{ minWidth: '80px' }}>
                      {visible ? word : '•••••'}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleWordVisibility(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.125rem',
                        fontSize: '0.875rem',
                        color: 'var(--color-text-secondary, #666)',
                      }}
                      aria-label={`${visible ? 'Masquer' : 'Afficher'} le mot ${index + 1}`}
                      title={`${visible ? 'Masquer' : 'Afficher'} le mot ${index + 1}`}
                    >
                      {visible ? '👁' : '👁‍🗨'}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ margin: 0, padding: '0.75rem' }}>—</p>
          )}
        </div>
        <p>
          <Link to="/">Accueil</Link> — <Link to="/manage-pairs">Gérer les pairs</Link>
        </p>
      </main>
    );
  }

  return (
    <main>
      <h1>Saisir les mots du 1ᵉʳ appareil</h1>
      <p>
        Saisissez les 17 mots affichés par le 1ᵉʳ appareil.
      </p>
      <form
        onSubmit={(ev) => void handleSubmit(ev)}
        aria-label="Saisir les mots du 1er appareil"
      >
        <label htmlFor="pairing-words-display">
          Mots du 1ᵉʳ appareil
                  <WordInputGrid
                    id="pairing-words-display"
                    value={wordInput}
                    onChange={setWordInput}
                    aria-describedby={error !== null ? 'pairing-display-err' : undefined}
                    aria-label="Saisir les 17 mots du 1er appareil"
                    wordCount={17}
                  />
        </label>
        {error !== null && (
          <p id="pairing-display-err" role="alert" style={{ color: 'var(--color-error)' }}>
            {error}
          </p>
        )}
        <button type="submit" disabled={isConfirming}>
          {isConfirming ? 'Finalisation…' : 'Valider'}
        </button>
      </form>
      <p>
        <Link to="/">Accueil</Link> — <Link to="/manage-pairs">Gérer les pairs</Link>
      </p>
    </main>
  );
}
